import { useMemo } from "react";

import { AssetsIcon, CanvasIcon, FileIcon } from "@hightouch/htui";

import type { AgentFocusFile } from "src/components/llm/agent-focus/agent-focus";
import type { ComposerContextChip } from "src/components/llm/components/composer-context-chip";
import { fileTypes } from "src/pages/projects/library/files/file-type";

const TYPE_ICONS = new Map(
  Object.entries(fileTypes).map(([type, { icon }]) => [type, icon]),
);

const SELECTION_HOSTS = {
  canvas: {
    one: "item",
    many: "items",
    icon: CanvasIcon,
    empty: "Ask about files on this canvas…",
  },
  library: {
    one: "file",
    many: "files",
    icon: AssetsIcon,
    empty: "Ask about files in your library…",
  },
} as const;

/** The page whose selection the chat shows; it names and draws a multi-item chip. */
export type SelectionHost = keyof typeof SELECTION_HOSTS;

export function selectionComposerPlaceholder(
  host: SelectionHost,
  itemCount: number,
): string {
  const { one, many, empty } = SELECTION_HOSTS[host];
  if (itemCount === 0) return empty;
  return itemCount === 1
    ? `Ask about this ${one}…`
    : `Ask about these ${itemCount} ${many}…`;
}

/**
 * The canvas or Library selection as one composer chip: the item itself, or
 * "N items" ("N files" in the Library) for several. Removing the chip clears the selection.
 */
export function useCanvasContextChips(
  selectedFiles: readonly Omit<AgentFocusFile, "nodeId">[],
  onClearSelection: () => void,
  host: SelectionHost,
): ComposerContextChip[] {
  return useMemo(() => {
    const [only] = selectedFiles;
    if (!only) return [];
    const { many, icon } = SELECTION_HOSTS[host];
    const chip =
      selectedFiles.length === 1
        ? {
            id: `canvas-item:${only.fileId}`,
            label: only.name || "Untitled",
            icon: TYPE_ICONS.get(only.type) ?? FileIcon,
          }
        : {
            id: "canvas-selection",
            label: `${selectedFiles.length} ${many}`,
            icon,
          };
    return [
      {
        ...chip,
        removeLabel: `Remove ${chip.label}`,
        onRemove: onClearSelection,
      },
    ];
  }, [selectedFiles, onClearSelection, host]);
}
